/**
 * PollenDotsConcepts — operator page for Tour Concepts (client-facing briefs
 * for international / HNI clients). Create concepts, edit title / subtitle /
 * body / "Prepared for", attach PDFs (each with its own title / subtitle /
 * body), copy the share link and see open counts.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import ConceptMarkdown from '@/components/ConceptMarkdown';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Plane, Plus, Copy, ExternalLink, Trash2, Eye, Loader2, FileText,
  ChevronDown, ChevronUp, Upload, Check, BookOpen, Image as ImageIcon, CopyPlus, GripVertical,
} from 'lucide-react';

const SHARE_BASE = (process.env.REACT_APP_PUBLIC_SHARE_DOMAIN || 'https://pollendots.com').replace(/\/$/, '');

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const jfetch = async (path, opts = {}) => {
  const r = await fetch(`${API}/pollen${path}`, {
    credentials: 'include',
    ...opts,
    headers: { ...(opts.body && !(opts.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}), ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Request failed');
  return r.json();
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

// ---- Per-PDF editor row ------------------------------------------------------
const FileRow = ({ cid, file, onChanged, onDeleted, onMove, canUp, canDown }) => {
  const [form, setForm] = useState({ title: file.title, subtitle: file.subtitle || '', body: file.body || '' });
  const [saving, setSaving] = useState(false);
  const dirty = form.title !== file.title || form.subtitle !== (file.subtitle || '') || form.body !== (file.body || '');
  const marker = `[[pdf:${file.id.slice(0, 6)}]]`;

  const copyMarker = async () => {
    try { await navigator.clipboard.writeText(marker); toast.success(`Marker copied — paste ${marker} into the body where this PDF should appear`); }
    catch { window.prompt('Copy this marker:', marker); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('PDF title cannot be empty'); return; }
    setSaving(true);
    try {
      await jfetch(`/concepts/${cid}/files/${file.id}`, { method: 'PATCH', body: JSON.stringify(form) });
      onChanged({ ...file, ...form });
      toast.success('PDF details saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove “${file.title}” from this concept?`)) return;
    try {
      await jfetch(`/concepts/${cid}/files/${file.id}`, { method: 'DELETE' });
      onDeleted(file.id);
      toast.success('PDF removed');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="rounded-lg border p-3 space-y-2 bg-white" data-testid={`concept-file-${file.id}`}>
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-muted-foreground/50 shrink-0 cursor-grab" />
        <FileText className="w-4 h-4 text-rose-800 shrink-0" />
        <span className="text-xs text-muted-foreground truncate flex-1">
          {file.original_filename} · {file.page_count} pages
        </span>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={!canUp}
                onClick={() => onMove(-1)} title="Move up" data-testid={`concept-file-up-${file.id}`}>
          <ChevronUp className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={!canDown}
                onClick={() => onMove(1)} title="Move down" data-testid={`concept-file-down-${file.id}`}>
          <ChevronDown className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
                onClick={remove} data-testid={`concept-file-delete-${file.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <span className="flex items-center gap-1 text-emerald-700 font-medium" data-testid={`concept-file-reads-${file.id}`}>
          <BookOpen className="w-3 h-3" />
          {file.read_count || 0} read{(file.read_count || 0) === 1 ? '' : 's'}
          {file.last_read_at ? ` · last ${fmtDate(file.last_read_at)}` : ''}
        </span>
        <button type="button" onClick={copyMarker}
                className="flex items-center gap-1 font-mono text-rose-800 hover:underline"
                title="Copy this marker, then paste it into the concept body where this PDF should appear"
                data-testid={`concept-file-marker-${file.id}`}>
          <Copy className="w-3 h-3" />{marker}
        </button>
      </div>
      <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
             placeholder="Document title" className="h-8 text-sm" data-testid={`concept-file-title-${file.id}`} />
      <Input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
             placeholder="Subtitle (optional)" className="h-8 text-sm" data-testid={`concept-file-subtitle-${file.id}`} />
      <Textarea rows={2} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="A short description shown under this document — Markdown supported (bold, italics, tables)"
                className="text-sm" data-testid={`concept-file-body-${file.id}`} />
      {dirty && (
        <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving} data-testid={`concept-file-save-${file.id}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Save</>}
        </Button>
      )}
    </div>
  );
};

// ---- One concept card (collapsed row → expanded editor) ---------------------
const ConceptCard = ({ concept, onUpdated, onDeleted, onDuplicated }) => {
  const [expanded, setExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [form, setForm] = useState({
    title: concept.title, subtitle: concept.subtitle || '',
    body: concept.body || '', prepared_for: concept.prepared_for || '',
  });
  const [files, setFiles] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasCover, setHasCover] = useState(!!concept.cover_path);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverStamp, setCoverStamp] = useState(0);
  const coverRef = useRef(null);
  const fileRef = useRef(null);
  const bodyRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);

  const reorder = (from, to) => {
    if (to < 0 || to >= (files || []).length) return;
    const list = [...files];
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    const stamped = list.map((f, i) => ({ ...f, sort_order: i }));
    setFiles(stamped);
    Promise.all(stamped.map((f, i) =>
      jfetch(`/concepts/${concept.id}/files/${f.id}`, { method: 'PATCH', body: JSON.stringify({ sort_order: i }) })
    )).then(() => toast.success('Order saved')).catch(() => toast.error('Could not save the order'));
  };

  const insertJourneysMarker = () => {
    const el = bodyRef.current;
    const pos = el && typeof el.selectionStart === 'number' ? el.selectionStart : form.body.length;
    const next = `${form.body.slice(0, pos)}\n\n[[journeys]]\n\n${form.body.slice(pos)}`;
    setForm(f => ({ ...f, body: next }));
    toast.success('Journeys section placed — now Save the concept');
  };

  const uploadCover = async (f) => {
    if (!f) return;
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await jfetch(`/concepts/${concept.id}/cover`, { method: 'POST', body: fd });
      setHasCover(true);
      setCoverStamp(Date.now());
      toast.success('Cover image set');
    } catch (e) { toast.error(e.message); }
    finally { setCoverBusy(false); if (coverRef.current) coverRef.current.value = ''; }
  };

  const removeCover = async () => {
    try {
      await jfetch(`/concepts/${concept.id}/cover`, { method: 'DELETE' });
      setHasCover(false);
      toast.success('Cover removed');
    } catch (e) { toast.error(e.message); }
  };
  const shareUrl = `${SHARE_BASE}/api/og/concept/${concept.slug}`;
  const dirty = form.title !== concept.title || form.subtitle !== (concept.subtitle || '')
    || form.body !== (concept.body || '') || form.prepared_for !== (concept.prepared_for || '');

  const loadFiles = useCallback(async () => {
    try {
      const d = await jfetch(`/concepts/${concept.id}`);
      setFiles(d.files || []);
    } catch { setFiles([]); }
  }, [concept.id]);

  useEffect(() => { if (expanded && files === null) loadFiles(); }, [expanded, files, loadFiles]);

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title cannot be empty'); return; }
    setSaving(true);
    try {
      await jfetch(`/concepts/${concept.id}`, { method: 'PATCH', body: JSON.stringify(form) });
      onUpdated({ ...concept, ...form });
      toast.success('Concept saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append('file', f);
        const doc = await jfetch(`/concepts/${concept.id}/files`, { method: 'POST', body: fd });
        setFiles(prev => [...(prev || []), doc]);
      }
      toast.success('PDF attached — add its title & description below');
      onUpdated({ ...concept, file_count: (concept.file_count || 0) + fileList.length });
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Share link copied'); }
    catch { window.prompt('Copy this link:', shareUrl); }
  };

  const [duplicating, setDuplicating] = useState(false);

  const duplicate = async () => {
    setDuplicating(true);
    try {
      const doc = await jfetch(`/concepts/${concept.id}/duplicate`, { method: 'POST' });
      onDuplicated(doc);
      toast.success('Concept duplicated — set the new client\u2019s name');
    } catch (e) { toast.error(e.message); }
    finally { setDuplicating(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete concept “${concept.title}”? Its share link stops working immediately.`)) return;
    try {
      await jfetch(`/concepts/${concept.id}`, { method: 'DELETE' });
      onDeleted(concept.id);
      toast.success('Concept deleted');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid={`concept-card-${concept.id}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-2">
          <button className="flex-1 min-w-0 text-left" onClick={() => setExpanded(v => !v)}
                  data-testid={`concept-toggle-${concept.id}`}>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-sm sm:text-base truncate">{concept.title}</span>
              {concept.prepared_for && (
                <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-800">
                  For {concept.prepared_for}
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
              <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{concept.view_count || 0} opens</span>
              <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{concept.file_count ?? (files || []).length} PDFs</span>
              <span>Last opened {fmtDate(concept.last_viewed_at)}</span>
            </p>
          </button>
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Duplicate for the next client"
                    onClick={duplicate} disabled={duplicating} data-testid={`concept-duplicate-${concept.id}`}>
              {duplicating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CopyPlus className="w-3.5 h-3.5" />}
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Copy share link"
                    onClick={copyLink} data-testid={`concept-copy-${concept.id}`}>
              <Copy className="w-3.5 h-3.5" />
            </Button>
            <a href={`/concept/${concept.slug}`} target="_blank" rel="noreferrer"
               className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"
               title="Preview" data-testid={`concept-preview-${concept.id}`}>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setExpanded(v => !v)}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 space-y-3 border-t pt-4">
            <p className="text-[11px] text-muted-foreground break-all">
              Open link: <span className="font-mono">{shareUrl}</span>
            </p>

            {/* Cover image — hero photo + link preview */}
            <div className="flex items-center gap-3">
              {hasCover ? (
                <img src={`${API}/public/concept/${concept.slug}/cover?t=${coverStamp}`} alt="Cover"
                     className="h-16 w-28 object-cover rounded-lg border" data-testid={`concept-cover-preview-${concept.id}`} />
              ) : (
                <div className="h-16 w-28 rounded-lg border border-dashed flex items-center justify-center text-muted-foreground">
                  <ImageIcon className="w-5 h-5" />
                </div>
              )}
              <div className="space-y-1">
                <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                       onChange={e => uploadCover(e.target.files?.[0])} data-testid={`concept-cover-input-${concept.id}`} />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={coverBusy}
                          onClick={() => coverRef.current?.click()} data-testid={`concept-cover-upload-${concept.id}`}>
                    {coverBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ImageIcon className="w-3 h-3 mr-1" />{hasCover ? 'Change cover' : 'Add cover image'}</>}
                  </Button>
                  {hasCover && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50"
                            onClick={removeCover} data-testid={`concept-cover-remove-${concept.id}`}>Remove</Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground">Shown at the top of the page and as the link preview image.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                       data-testid={`concept-title-${concept.id}`} />
              </div>
              <div>
                <Label className="text-xs">Prepared for</Label>
                <Input value={form.prepared_for} onChange={e => setForm(f => ({ ...f, prepared_for: e.target.value }))}
                       placeholder="e.g. Mr. & Mrs. Rothschild" data-testid={`concept-preparedfor-${concept.id}`} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Subtitle</Label>
              <Input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
                     placeholder="One graceful line under the title" data-testid={`concept-subtitle-${concept.id}`} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Body</Label>
                <button type="button" onClick={() => setShowPreview(v => !v)}
                        className="text-[11px] text-rose-800 hover:underline"
                        data-testid={`concept-preview-toggle-${concept.id}`}>
                  {showPreview ? 'Back to editing' : 'Preview formatting'}
                </button>
              </div>
              {showPreview ? (
                <div className="mt-1 border rounded-lg p-4 bg-[#FCF8F7] max-h-96 overflow-y-auto" data-testid={`concept-preview-${concept.id}`}>
                  <ConceptMarkdown>{form.body}</ConceptMarkdown>
                </div>
              ) : (
                <>
                  <Textarea rows={8} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                            ref={bodyRef}
                            placeholder={'The concept narrative. Markdown supported.'}
                            className="font-mono text-xs" data-testid={`concept-body-${concept.id}`} />
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <Button type="button" size="sm" variant="outline" className="h-6 text-[11px]"
                            onClick={insertJourneysMarker} data-testid={`concept-insert-journeys-${concept.id}`}>
                      <Plus className="w-3 h-3 mr-1" />Place journeys section at cursor
                    </Button>
                    <p className="text-[10px] text-muted-foreground">
                      Click inside the body where the itinerary cards should appear, then tap this. Remember to Save.
                    </p>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Markdown supported: # headings, **bold**, *italics*, lists, --- dividers and | tables |. Or paste a single PDF's <span className="font-mono">[[pdf:...]]</span> marker (copy from the PDF list below) to place one card.
                  </p>
                </>
              )}
            </div>
            {dirty && (
              <Button size="sm" onClick={save} disabled={saving} data-testid={`concept-save-${concept.id}`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Save concept</>}
              </Button>
            )}

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Tour PDFs — day-wise itineraries</Label>
                <input ref={fileRef} type="file" accept="application/pdf" multiple className="hidden"
                       onChange={e => upload(Array.from(e.target.files || []))}
                       data-testid={`concept-file-input-${concept.id}`} />
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={uploading}
                        onClick={() => fileRef.current?.click()} data-testid={`concept-upload-${concept.id}`}>
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" />Attach PDF</>}
                </Button>
              </div>
              {files === null ? (
                <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
              ) : files.length === 0 ? (
                <p className="text-xs text-muted-foreground">No PDFs attached yet.</p>
              ) : (
                <div className="space-y-2">
                  {files.map((f, i) => (
                    <div key={f.id} draggable
                         onDragStart={() => setDragIdx(i)}
                         onDragOver={(e) => e.preventDefault()}
                         onDrop={() => { if (dragIdx !== null && dragIdx !== i) reorder(dragIdx, i); setDragIdx(null); }}
                         data-testid={`concept-file-wrap-${f.id}`}>
                      <FileRow cid={concept.id} file={f}
                               canUp={i > 0} canDown={i < files.length - 1}
                               onMove={(dir) => reorder(i, i + dir)}
                               onChanged={(nf) => setFiles(prev => prev.map(x => x.id === nf.id ? nf : x))}
                               onDeleted={(fid) => setFiles(prev => prev.filter(x => x.id !== fid))} />
                    </div>
                  ))}
                  <p className="text-[10px] text-muted-foreground">Drag rows (or use the arrows) to set the order clients see the itineraries.</p>
                </div>
              )}
            </div>

            <div className="border-t pt-3">
              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={remove} data-testid={`concept-delete-${concept.id}`}>
                <Trash2 className="w-3 h-3 mr-1" />Delete concept
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function PollenDotsConcepts() {
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', subtitle: '', prepared_for: '', body: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try { setConcepts(await jfetch('/concepts')); }
      catch { toast.error('Could not load concepts'); }
      finally { setLoading(false); }
    })();
  }, []);

  const create = async () => {
    if (!createForm.title.trim()) { toast.error('Give the concept a title'); return; }
    setCreating(true);
    try {
      const doc = await jfetch('/concepts', { method: 'POST', body: JSON.stringify(createForm) });
      setConcepts(prev => [{ ...doc, file_count: 0 }, ...prev]);
      setCreateOpen(false);
      setCreateForm({ title: '', subtitle: '', prepared_for: '', body: '' });
      toast.success('Concept created — attach PDFs and share the link');
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  return (
    <Layout>
      <div className="max-w-3xl space-y-4" data-testid="concepts-page">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Plane className="w-6 h-6 text-rose-800" />Tour Concepts
            </h1>
            <p className="text-sm text-muted-foreground">
              Personalised concept briefs for prospective clients — share as a link, track every open.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="concept-new-btn">
            <Plus className="w-4 h-4 mr-1" />New Concept
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : concepts.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <Plane className="w-10 h-10 text-rose-200 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No concepts yet — create the first brief for a client.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {concepts.map(c => (
              <ConceptCard key={c.id} concept={c}
                onUpdated={(nc) => setConcepts(prev => prev.map(x => x.id === nc.id ? nc : x))}
                onDeleted={(id) => setConcepts(prev => prev.filter(x => x.id !== id))}
                onDuplicated={(doc) => setConcepts(prev => [doc, ...prev])} />
            ))}
          </div>
        )}

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Plane className="w-4 h-4 text-rose-800" />New Tour Concept</DialogTitle>
              <DialogDescription>A personalised brief page — you can attach PDFs after creating it.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                       placeholder="e.g. Bhutan — A Kingdom in Slow Motion" data-testid="concept-create-title" />
              </div>
              <div>
                <Label className="text-xs">Subtitle</Label>
                <Input value={createForm.subtitle} onChange={e => setCreateForm(f => ({ ...f, subtitle: e.target.value }))}
                       placeholder="One graceful line under the title" data-testid="concept-create-subtitle" />
              </div>
              <div>
                <Label className="text-xs">Prepared for</Label>
                <Input value={createForm.prepared_for} onChange={e => setCreateForm(f => ({ ...f, prepared_for: e.target.value }))}
                       placeholder="Client's name" data-testid="concept-create-preparedfor" />
              </div>
              <div>
                <Label className="text-xs">Body</Label>
                <Textarea rows={4} value={createForm.body} onChange={e => setCreateForm(f => ({ ...f, body: e.target.value }))}
                          placeholder="The concept narrative — Markdown supported (# headings, **bold**, tables, ---)."
                          data-testid="concept-create-body" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={creating} data-testid="concept-create-save">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create concept'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
